import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import {
  isActiveMatchStatus,
  MatchEndReasonSchema,
  MatchStatusSchema,
} from '../match-status/match-status';

/**
 * One match, as the member looking at it reads it.
 *
 * **`otherUserId`, never the stored pair.** The row is `(memberAId, memberBId)`
 * in canonical order so the database can refuse a duplicate; which of the two
 * the reader *is* is not information the reader needs, and a payload carrying
 * both would put "work out which one is me" in every client that renders a
 * match.
 *
 * The list carries **ids and timestamps only** — no answers, no photographs. A
 * client hydrates each row through the visibility resolver (N18), which is what
 * keeps the matching module out of the business of deciding what a member may
 * see of another.
 *
 * The refinement pins `status` to the ending fields: an `ended` match that
 * cannot say when it ended is a row nobody can explain to the member, and an
 * `active` one carrying an `endedAt` is a row two writers disagreed about.
 */
export const MatchSchema = z
  .object({
    id: UuidSchema,
    otherUserId: UuidSchema,
    status: MatchStatusSchema,
    matchedAt: z.iso.datetime(),
    endedAt: z.iso.datetime().nullable(),
    endedReason: MatchEndReasonSchema.nullable(),
  })
  .strict()
  .refine((match) => isActiveMatchStatus(match.status) === (match.endedAt === null), {
    message: 'An ended match records when it ended; an active one records nothing.',
    path: ['endedAt'],
  })
  .refine((match) => isActiveMatchStatus(match.status) === (match.endedReason === null), {
    message: 'An ended match records why it ended; an active one has no reason to give.',
    path: ['endedReason'],
  });

export type Match = z.infer<typeof MatchSchema>;
